import React, { FC, ReactNode, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { Banner } from '@trip-ux/components/banner';
import { Footer, FooterProps, LegacyFooter, LegacyFooterProps } from '@trip-ux/components/footer';
import { ResponsiveTopNav, ResponsiveTopNavProps } from '@trip-ux/components/responsive-top-nav';
import { ReduxStore, isReferenceId } from '@trip-ux/redux-store';
import { AUTH_HEADERS, COVERME_ORG } from '@trip-ux/constants';
import { showComponent } from '@trip-ux/common';
import { MuxAdminLayout } from './MuxAdminLayout.mux';
import { NeedAssistance } from './NeedAssistance';
import './coverme-mux-layout.scss';

export interface CovermeMuxLayoutProps {
  children: ReactNode;
  isAdmin: boolean;
  isLIAUser: boolean;
  onLanguageToggle: (e: React.MouseEvent) => void;
  locale: string;
  langAriaLabel: string;
  shouldShowNeedAssistance: boolean;
}

const prepareMenus = (
  topNav: Record<string, unknown>,
  branding: unknown,
  showFullNavBar: boolean
): Record<string, unknown> => {
  const isValidTopNav = typeof topNav === 'object' && topNav !== null;
  const baseMenus = { ...(isValidTopNav ? topNav : {}), branding };
  return showFullNavBar ? { ...baseMenus, menuList: [], contactUs: {} } : baseMenus;
};
/**
 * MUX-based responsive layout for CoverMe booking flow
 */
export const CovermeMuxLayout: FC<CovermeMuxLayoutProps> = ({
  children,
  isAdmin,
  onLanguageToggle,
  locale,
  langAriaLabel,
  shouldShowNeedAssistance
}) => {
  const { contentFragment, config, userProfile, whiteLabel } = useSelector((store: ReduxStore) => ({
    contentFragment: store.contentFragment,
    config: store.config,
    userProfile: store.userProfile,
    whiteLabel: store.userProfile.whiteLabel
  }));

  const isB2C = config.options.salesChannel === AUTH_HEADERS.SALES_CHANNEL_B2C;
  const referenceId = isReferenceId(whiteLabel?.referenceId);
  const hasRefCode = Boolean(whiteLabel?.referenceId) && whiteLabel?.referenceId !== COVERME_ORG;
  const isCoverMe = userProfile.organizationCode === COVERME_ORG;

  const useLegacyFooter = isCoverMe && isB2C && !hasRefCode;
  const useMuxFooter = isAdmin || hasRefCode;
  // Show full nav bar only for refCode users - CoverMe B2C should have collapsed nav
  const showFullNavBar = hasRefCode;
  const isCollapsed = !showFullNavBar;
  const refIdProp = hasRefCode || referenceId;

  const organizationName = useMemo(() => {
    const orgNameKey = `organizationName${config.locale.toUpperCase()}`;
    return `${get(userProfile, orgNameKey, '')} (${userProfile.organizationCode})`;
  }, [config.locale, userProfile]);

  const menus = useMemo(
    () => prepareMenus(
      contentFragment.topNav as Record<string, unknown>,
      contentFragment.banner['whiteLabelBranding'],
      showFullNavBar
    ),
    [contentFragment.topNav, contentFragment.banner, showFullNavBar]
  );

  const handleOnSelect = useCallback(() => true, []);

  if (isAdmin) {
    return <MuxAdminLayout>{children}</MuxAdminLayout>;
  }

  return (
    <>
      {showFullNavBar && (
        <ResponsiveTopNav
          {...(menus as unknown as ResponsiveTopNavProps)}
          languageInfo={{
            navId: `${locale}-element`,
            ariaLabel: langAriaLabel,
            label: locale.toUpperCase(),
            hrefLang: locale,
            href: '',
            isVisible: !referenceId || hasRefCode
          }}
          onSelect={handleOnSelect}
          isCollapsed={isCollapsed}
          isB2C={isB2C}
          referenceId={refIdProp}
          organizationCode={userProfile.organizationCode}
          onLanguageToggle={onLanguageToggle}
        />
      )}

      <Banner
        whiteLabelBranding={get(contentFragment.banner, 'whiteLabelBranding')}
        variant="SECONDARY"
        manulifeBranding={get(contentFragment.banner, 'manulifeBranding')}
        organizationCategory={userProfile.organizationCategory}
        hasLogo={!!userProfile.whiteLabel?.logoFile}
        isCollapsed={isCollapsed}
        isB2C={isB2C}
        referenceId={refIdProp}
        organizationName={organizationName}
        userProfile={userProfile}
      />

      <div className="coverme-content-wrapper">
        {children}
        {showComponent(shouldShowNeedAssistance, (
          <div className="mux-GridContainer py-4">
            <div className="mux-grid mux-grid-cols-4 tablet:mux-grid-cols-12 mux-col-span-full mux-gap-6">
              <div className="mux-col-span-full xl:mux-col-span-10 xl:mux-col-start-3 px-4 xl:px-0">
                <NeedAssistance />
              </div>
            </div>
          </div>
        ))}
        {showComponent(useLegacyFooter, <LegacyFooter {...(contentFragment.footer as LegacyFooterProps)} />)}
        {showComponent(useMuxFooter, <Footer {...(contentFragment.footer as FooterProps)} />)}
      </div>
    </>
  );
};

CovermeMuxLayout.displayName = 'CovermeMuxLayout';
export default CovermeMuxLayout;
